/**
 * @tt/lobby/season — season + daily challenge constants and helpers.
 *
 * Season XP curve: 60 tiers × 1000 XP each. Earned on every round
 * alongside Account XP (server-side in finalizeMatch).
 *
 * Daily challenges: server picks 3 each day from a ~10-template pool.
 * 1 free reroll/day. Refresh at local 04:00 — for now we treat all
 * dates as UTC and let the client adjust.
 */

export const CURRENT_SEASON_NUMBER = 1;
export const SEASON_TIER_XP = 1000;
export const SEASON_TOTAL_TIERS = 60;
export const DAILY_CHALLENGE_COUNT = 3;
export const DAILY_REROLLS_PER_DAY = 1;

export interface SeasonProgress {
  season: number;
  tier: number;
  seasonXp: number;
  /** XP within the current tier. */
  current: number;
  /** XP needed to advance from this tier. */
  needed: number;
  claimed: boolean[];
}

export function tierFromSeasonXp(xp: number): { tier: number; current: number; needed: number } {
  const tier = Math.min(SEASON_TOTAL_TIERS, Math.floor(xp / SEASON_TIER_XP));
  const current = xp - tier * SEASON_TIER_XP;
  const needed = tier >= SEASON_TOTAL_TIERS ? 0 : SEASON_TIER_XP;
  return { tier, current, needed };
}

/** A single reward awarded by claiming a season-pass tier. The `ref` is
 *  interpreted by `kind`:
 *    - 'lantern' / 'embers' → numeric quantity
 *    - 'cosmetic'           → cosmetic id from `COSMETIC_CATALOG`
 *    - 'title'              → title string
 *    - 'boost'              → boost id (e.g. `boost-2x-24h`)
 *
 *  Rarity 5 = mythic (premium-track capstones). The shape is a single
 *  reward per tier per track — the full season payout for a tier is the
 *  free entry PLUS the premium entry (when the player owns the pass). */
export interface SeasonReward {
  tier: number;
  kind: 'cosmetic' | 'embers' | 'lantern' | 'title' | 'boost';
  ref: string | number;
  label: string;
  rarity: 1 | 2 | 3 | 4 | 5;
}

/** A complete season — one free track + one premium track. Both arrays
 *  are 60 entries long, indexed [0..59] for tiers 1..60. */
export interface SeasonRewardTrack {
  free: SeasonReward[];
  premium: SeasonReward[];
}

/** Free-track lantern drops by their position in the ordered list. The
 *  20 lantern tiers receive these values in order (smallest → largest)
 *  so progression always feels rewarding. Sum = 3,650 lanterns. */
const SEASON_1_FREE_LANTERN_VALUES = [25, 25, 50, 50, 75, 75, 100, 100, 100, 100, 100, 150, 150, 200, 200, 250, 300, 400, 500, 700];

/** Premium-track BONUS lantern drops (additive on top of the free track).
 *  Listed sum: 12,725 lanterns. The accompanying spec describes this set
 *  as "totaling ~12,775" — we ship the values verbatim from the table. */
const SEASON_1_PREMIUM_LANTERN_VALUES = [50, 50, 75, 100, 100, 150, 150, 200, 250, 300, 300, 400, 500, 600, 800, 1000, 1200, 1500, 2000, 3000];

/** Tiers (1-indexed) that receive a free-track lantern drop. 20 tiers. */
const SEASON_1_FREE_LANTERN_TIERS = [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 39, 42, 45, 48, 51, 54, 57, 60];

/** Free-track miscellaneous (embers / title) tiers. 10 tiers. */
const SEASON_1_FREE_MISC: Array<SeasonReward> = [
  { tier: 1,  kind: 'embers', ref: 300,  label: '300 Embers',                 rarity: 1 },
  { tier: 5,  kind: 'embers', ref: 500,  label: '500 Embers',                 rarity: 1 },
  { tier: 10, kind: 'title',  ref: 'Season 1 Patron', label: 'Title: Season 1 Patron', rarity: 2 },
  { tier: 13, kind: 'embers', ref: 500,  label: '500 Embers',                 rarity: 1 },
  { tier: 20, kind: 'embers', ref: 750,  label: '750 Embers',                 rarity: 2 },
  { tier: 25, kind: 'embers', ref: 750,  label: '750 Embers',                 rarity: 2 },
  { tier: 35, kind: 'embers', ref: 1000, label: '1,000 Embers',               rarity: 3 },
  { tier: 40, kind: 'embers', ref: 1000, label: '1,000 Embers',               rarity: 3 },
  { tier: 50, kind: 'embers', ref: 1500, label: '1,500 Embers',               rarity: 3 },
  { tier: 55, kind: 'title',  ref: 'Season 1 Devotee', label: 'Title: Season 1 Devotee', rarity: 3 },
];

/** Tiers (1-indexed) that receive a premium-track BONUS lantern drop. 20 tiers. */
const SEASON_1_PREMIUM_LANTERN_TIERS = [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34, 37, 40, 43, 46, 49, 52, 55, 58];

/** Premium-track miscellaneous tiers. 10 tiers (boost / embers / title /
 *  Crimson Solstice cosmetics). Phase E1 — pinned tiers 20/30/40/50/60
 *  to the Crimson Solstice five-piece cosmetics (body/border/hat/
 *  background/face). Tier 60's Searing Mark face is the final-claim
 *  capstone — the season-claim Lambda also grants the title
 *  `Lantern Bearer of the Crimson Solstice` alongside the face (see
 *  docs/phase-e-cosmetics.md §"Crimson Solstice mythic title").
 *  Tier 59 retains the 'Crimson Solstice' title flair. The tier-30 + 50
 *  slots used to be embers / background-placeholder respectively; we
 *  override both to make room for the Ember Ring border + Forge's Maw
 *  background cosmetics. The displaced 1,000 embers becomes a placeholder
 *  cosmetic via the fill loop below. */
const SEASON_1_PREMIUM_MISC: Array<SeasonReward> = [
  { tier: 5,  kind: 'boost',    ref: 'boost-2x-24h',     label: '2x XP Boost (24h)',        rarity: 3 },
  { tier: 11, kind: 'boost',    ref: 'boost-2x-24h',     label: '2x XP Boost (24h)',        rarity: 3 },
  // Phase F-Foundation: Crimson Solstice five-piece refs were renamed
  // from semantic ids (`solstice-bearer-body`, `crown-of-coals`,
  // `searing-mark`) to numeric `body-${idx}` / `hat-${idx}` /
  // `face-${idx}` matching their position in BODY/HAT/FACE_SHAPES.
  // Border + background ids stay semantic.
  { tier: 20, kind: 'cosmetic', ref: 'body-8',           label: 'Solstice Bearer Body',     rarity: 5 },
  { tier: 30, kind: 'cosmetic', ref: 'ember-ring',       label: 'Ember Ring Border',        rarity: 5 },
  { tier: 38, kind: 'boost',    ref: 'boost-2x-48h',     label: '2x XP Boost (48h)',        rarity: 4 },
  { tier: 40, kind: 'cosmetic', ref: 'hat-8',            label: 'Crown of Coals',           rarity: 5 },
  { tier: 44, kind: 'embers',   ref: 1500,               label: '1,500 Embers',             rarity: 3 },
  { tier: 50, kind: 'cosmetic', ref: 'forges-maw',       label: "Forge's Maw Background",   rarity: 5 },
  { tier: 54, kind: 'boost',    ref: 'boost-2x-72h',     label: '2x XP Boost (72h)',        rarity: 4 },
  { tier: 59, kind: 'title',    ref: 'Crimson Solstice', label: 'Title: Crimson Solstice',  rarity: 5 },
  // Tier 60 capstone — the Searing Mark face. The claim handler also
  // grants the 'Lantern Bearer of the Crimson Solstice' title (logic
  // lives in lambdas/season; this entry is the cosmetic side).
  { tier: 60, kind: 'cosmetic', ref: 'face-8',           label: 'Searing Mark',             rarity: 5 },
];

// ─────────────────────────────────────────────────────────────────────
// Phase E1 — Per-tier cosmetic refs for the 30 free + 30 premium slots
// not already filled by lantern/misc tiers.
//
// Free-track refs: a mix of EXISTING items (rebadged so they appear in
// the season pass — players who already own the item still see the
// claim succeed and the duplicate is deduped server-side) and PLANNED
// new ids that E2/E3 will register in the catalog. The "rebadge"
// pattern follows the doc's note that some catalog entries cover
// multiple acquisition lanes (a body unlocked at level 12 can also be
// a tier-15 season-free reward — both sources resolve to the same
// inventory id).
//
// Premium-track refs: every entry is `season-premium` source. Most are
// PLANNED new ids E3 will create (semantic names like
// `crimson-flame-body`, `embercloak-hat`); the five-piece Crimson
// Solstice items live in SEASON_1_PREMIUM_MISC (tiers 20/30/40/50/60),
// not here.
//
// IMPORTANT: every ref below must eventually exist in COSMETIC_CATALOG.
// E1 ships with the existing-item refs already registered. E2/E3 close
// the loop by appending their semantic-id entries. `tools/validate-
// catalog.mjs` (E4) checks that every season ref has a catalog entry.
// ─────────────────────────────────────────────────────────────────────

interface CosmeticTierFill { ref: string; label: string }

/** Free-track cosmetic refs by tier. Tiers 1/3/5/6/9/10/12/13/15/18/20/
 *  21/24/25/27/30/33/35/36/39/40/42/45/48/50/51/54/55/57/60 are filled
 *  by lantern or misc rewards — those tiers are absent here. */
const SEASON_1_FREE_COSMETIC_FILL: Record<number, CosmeticTierFill> = {
  // Phase F5 — 7 of the 12 rebadge slots are now PRIMARY-SOURCED season
  // tracks for new seat cosmetics + masks (mardi-gras, tide-foam,
  // garden, tide-pool, confetti, gilded-rose, oni). Each is the
  // canonical season-free unlock path for that catalog id (their
  // `source.kind === 'season-free'`), not a rebadge — claiming the tier
  // is the only free way to acquire them.
  2:  { ref: 'face-3',                    label: 'Stern Face' },             // rebadge (r2)
  4:  { ref: 'mask-mardi-gras',           label: 'Mardi Gras Mask' },        // F5 mask (r3)
  7:  { ref: 'seat-border-tide-foam',     label: 'Tide Foam Seat Border' },  // F5 seat (r3)
  8:  { ref: 'seat-background-garden',    label: 'Garden Seat Backdrop' },   // F5 seat (r3)
  11: { ref: 'seat-background-tide-pool', label: 'Tide Pool Seat Backdrop' },// F5 seat (r3)
  14: { ref: 'seat-particle-confetti',    label: 'Confetti Seat Particles' },// F5 seat (r3)
  16: { ref: 'seat-border-gilded-rose',   label: 'Gilded Rose Seat Border' },// F5 seat (r4)
  17: { ref: 'gilded-rose-ring',          label: 'Gilded Rose Ring Border' },// F6 NEW avatar border (r4)
  19: { ref: 'face-5',                    label: 'Mischief Face' },          // rebadge
  22: { ref: 'mask-oni',                  label: 'Oni Mask' },               // F5 mask (r4)
  23: { ref: 'hat-6',                     label: 'Flower Hat' },             // rebadge
  26: { ref: 'phoenix-feather',           label: 'Phoenix Feather Border' }, // F6 NEW avatar border (r4)

  // Phase F-Foundation: refs renamed from semantic ids to the new
  // numeric `body-${idx}` / `hat-${idx}` / `face-${idx}` form. Mapping
  // (semantic → numeric, by BODY_SHAPES / HAT_SHAPES / FACE_SHAPES idx):
  //   fox-body=body-9, wolf-body=body-10, raven-body=body-11,
  //   owl-body=body-12, jellyfish-body=body-13, candle-body=body-14,
  //   lantern-body=body-15, broom-body=body-16, cauldron-body=body-17,
  //   scroll-body=body-18; tricorn-hat=hat-9, witch-hat=hat-10,
  //   brass-goggles-hat=hat-11, antler-circlet-hat=hat-12,
  //   candle-cap-hat=hat-14; single-eye-face=face-9,
  //   third-eye-face=face-10, calligraphic-face=face-11.
  //
  // Catalog source per ref (unchanged from E2):
  //   season-free: body-9, body-10, body-11, body-13, hat-9, hat-10, face-10
  //   lantern-shop rebadges: body-12, body-14, body-15, body-16, body-17,
  //                body-18, hat-11, hat-12, hat-14, face-9, face-11
  //
  // halo-hat (hat-13) and ghost-veil-face (face-12) are season-premium
  // and live in SEASON_1_PREMIUM_COSMETIC_FILL below — NOT here.
  28: { ref: 'body-9',   label: 'Fox Body' },
  29: { ref: 'hat-9',    label: 'Tricorn Hat' },
  31: { ref: 'body-10',  label: 'Wolf Body' },
  32: { ref: 'hat-10',   label: 'Witch Hat' },
  34: { ref: 'face-10',  label: 'Third-Eye Face' },
  37: { ref: 'body-11',  label: 'Raven Body' },
  38: { ref: 'hat-11',   label: 'Brass Goggles' },     // rebadge (lantern-shop)
  41: { ref: 'body-13',  label: 'Jellyfish Body' },
  43: { ref: 'hat-12',   label: 'Antler Circlet' },    // rebadge
  44: { ref: 'face-11',  label: 'Calligraphic Face' }, // rebadge
  46: { ref: 'body-14',  label: 'Living Candle Body' },// rebadge
  47: { ref: 'body-15',  label: 'Lantern Body' },      // rebadge (was halo-hat placeholder; halo is premium)
  49: { ref: 'body-17',  label: 'Cauldron Body' },     // rebadge
  52: { ref: 'body-16',  label: 'Broom Body' },        // rebadge
  53: { ref: 'hat-14',   label: 'Candle Cap' },        // rebadge
  56: { ref: 'body-18',  label: 'Scroll Body' },       // rebadge
  58: { ref: 'face-9',   label: 'Single Eye Face' },   // rebadge
  59: { ref: 'body-12',  label: 'Owl Body' },          // rebadge (was ghost-veil placeholder; ghost-veil is premium)
};

/** Premium-track cosmetic refs by tier. Tiers 20/30/40/50/60 (Crimson
 *  Solstice five-piece) live in SEASON_1_PREMIUM_MISC. Lantern tiers
 *  and other misc tiers are filled by their own arrays.
 *
 *  Phase E4 cleanup: 28 dangling premium-track placeholder ids
 *  ('embercloak-hat', 'forge-glow-face', 'crimson-flame-body', etc.)
 *  were repointed to REAL catalog entries — most are lantern-shop or
 *  achievement-source items that the premium track grants for free as a
 *  perk of holding the season pass. The 7 truly premium-exclusive items
 *  (Crimson Solstice five-piece + halo-hat + ghost-veil-face) remain
 *  `source.kind = 'season-premium'` in COSMETIC_CATALOG. The other 28
 *  premium-track tiers are "rebadged" — premium owners get faster
 *  access to items also obtainable through other lanes (similar to
 *  how the free track rebadges achievement/level body unlocks).
 *  validate-catalog.mjs enforces every ref resolves in the catalog. */
const SEASON_1_PREMIUM_COSMETIC_FILL: Record<number, CosmeticTierFill> = {
  // 7 premium-exclusive entries — all `source.kind = 'season-premium'`
  // in COSMETIC_CATALOG. Crimson Solstice five-piece sits in
  // SEASON_1_PREMIUM_MISC at tiers 20/30/40/50/60; halo + ghost-veil
  // are here.
  //
  // Phase F-Foundation: refs renamed from semantic ids to numeric
  // `face-${idx}` / `hat-${idx}` form per the new id convention.
  // ghost-veil-face → face-12, halo-hat → hat-13.
  8:  { ref: 'face-12',  label: 'Ghost-Veil Face' },
  47: { ref: 'hat-13',   label: 'Halo Hat' },

  // 28 rebadged tiers — premium owners get bonus free access to items
  // that ALSO exist via lantern-shop / achievement / level lanes. The
  // catalog source.kind for these ids is NOT 'season-premium' — the
  // premium pass simply grants the item directly to inventory on claim.
  // Themed loosely toward the Crimson Solstice / hearth / forge palette
  // where catalog inventory permits.
  //
  // Phase F-Foundation: E2's semantic ids renamed to numeric form.
  // candle-cap-hat=hat-14, calligraphic-face=face-11,
  // candle-body=body-14, brass-goggles-hat=hat-11,
  // lantern-body=body-15, single-eye-face=face-9,
  // antler-circlet-hat=hat-12, cauldron-body=body-17.
  // Tier 57 was previously `body-9` "Owl Body" — that catalog id now
  // means Fox; repointed to body-12 (Owl Form, the new lantern-shop
  // owl variant) so the premium label still matches the visual.
  2:  { ref: 'hat-14',           label: 'Candle Cap' },             // lantern-shop
  3:  { ref: 'face-11',          label: 'Calligraphic Face' },      // lantern-shop
  6:  { ref: 'body-14',          label: 'Living Candle Body' },     // lantern-shop
  9:  { ref: 'hat-11',           label: 'Brass Goggles' },          // lantern-shop
  12: { ref: 'body-15',          label: 'Lantern Body' },           // lantern-shop
  14: { ref: 'face-9',           label: 'Single Eye Face' },        // lantern-shop
  15: { ref: 'hat-12',           label: 'Antler Circlet' },         // lantern-shop
  17: { ref: 'body-17',          label: 'Cauldron Body' },          // lantern-shop
  18: { ref: 'body-104',                 label: 'Living Flame Body' },          // achievement (dawn-streak)
  21: { ref: 'seat-border-solstice-flame', label: 'Solstice Flame Seat Border' },// F5 premium-only (r5, crimson-solstice)
  23: { ref: 'hat-7',            label: 'Candle Hat' },             // level
  24: { ref: 'face-7',           label: 'Cyclops Face' },           // achievement (club-1k)
  26: { ref: 'hat-2',            label: 'Crown' },                  // level
  27: { ref: 'tavern-hearth',    label: 'Tavern Hearth Backdrop' }, // lantern-shop
  29: { ref: 'face-6',           label: 'Sad Face' },               // level
  32: { ref: 'body-100',         label: 'Floating Orb Body' },      // achievement (play-200)
  33: { ref: 'candle-circle',    label: 'Candle Circle Border' },   // lantern-shop
  35: { ref: 'seat-background-forge-glow', label: 'Forge Glow Seat Backdrop' },  // F5 premium-only (r5, crimson-solstice)
  36: { ref: 'hat-1',            label: 'Wizard Hat' },             // achievement (first-win)
  39: { ref: 'forest-grove',     label: 'Forest Grove Backdrop' },  // lantern-shop
  41: { ref: 'face-3',           label: 'Stern Face' },             // level
  42: { ref: 'leaf-ring',        label: 'Leaf Ring Border' },       // lantern-shop
  45: { ref: 'seat-particle-runes',      label: 'Runes Seat Particles' },       // F5 premium-only (r4, crimson-solstice)
  48: { ref: 'hat-5',            label: 'Antlers' },                // achievement (streak-3)
  51: { ref: 'gemstone-studded', label: 'Gemstone Border' },        // lantern-shop
  53: { ref: 'mask-crimson-veil',        label: 'Crimson Veil Mask' },          // F5 premium-only (r5, crimson-solstice)
  56: { ref: 'hat-6',            label: 'Flower Hat' },             // level
  57: { ref: 'body-12',          label: 'Owl Form Body' },          // lantern-shop (was achievement body-9 = Owl)
};

/** Build one complete season track. Free + premium are both produced
 *  here so the placement logic stays in one place. */
function buildSeason1(): SeasonRewardTrack {
  const free: Array<SeasonReward | null> = Array.from({ length: SEASON_TOTAL_TIERS }, () => null);
  const premium: Array<SeasonReward | null> = Array.from({ length: SEASON_TOTAL_TIERS }, () => null);

  // Free track ─────────────────────────────────────────────────────────
  for (let i = 0; i < SEASON_1_FREE_LANTERN_TIERS.length; i++) {
    const tier = SEASON_1_FREE_LANTERN_TIERS[i] as number;
    const value = SEASON_1_FREE_LANTERN_VALUES[i] as number;
    free[tier - 1] = {
      tier,
      kind: 'lantern',
      ref: value,
      label: `${value} Lanterns`,
      rarity: value >= 500 ? 4 : value >= 200 ? 3 : value >= 100 ? 2 : 1,
    };
  }
  for (const m of SEASON_1_FREE_MISC) free[m.tier - 1] = m;
  // Fill the remaining 30 tiers with real / planned-real cosmetic refs
  // (Phase E1). Every ref must resolve in COSMETIC_CATALOG once E2/E3
  // append their entries.
  for (let t = 1; t <= SEASON_TOTAL_TIERS; t++) {
    if (free[t - 1] !== null) continue;
    const fill = SEASON_1_FREE_COSMETIC_FILL[t];
    if (!fill) {
      // Defensive — every gap should have an explicit fill. If a future
      // misc edit removes a tier without adding it here, fall back to a
      // benign embers reward rather than crash the build.
      free[t - 1] = { tier: t, kind: 'embers', ref: 250, label: '250 Embers', rarity: 1 };
      continue;
    }
    free[t - 1] = {
      tier: t,
      kind: 'cosmetic',
      ref: fill.ref,
      label: fill.label,
      rarity: t >= 50 ? 4 : t >= 30 ? 3 : t >= 15 ? 2 : 1,
    };
  }

  // Premium track ──────────────────────────────────────────────────────
  for (let i = 0; i < SEASON_1_PREMIUM_LANTERN_TIERS.length; i++) {
    const tier = SEASON_1_PREMIUM_LANTERN_TIERS[i] as number;
    const value = SEASON_1_PREMIUM_LANTERN_VALUES[i] as number;
    premium[tier - 1] = {
      tier,
      kind: 'lantern',
      ref: value,
      label: `${value} Bonus Lanterns`,
      rarity: value >= 1000 ? 5 : value >= 500 ? 4 : value >= 200 ? 3 : 2,
    };
  }
  for (const m of SEASON_1_PREMIUM_MISC) premium[m.tier - 1] = m;
  // Remaining tiers: premium-exclusive cosmetic refs (planned for E2/E3).
  for (let t = 1; t <= SEASON_TOTAL_TIERS; t++) {
    if (premium[t - 1] !== null) continue;
    const fill = SEASON_1_PREMIUM_COSMETIC_FILL[t];
    if (!fill) {
      premium[t - 1] = { tier: t, kind: 'embers', ref: 500, label: '500 Embers', rarity: 2 };
      continue;
    }
    premium[t - 1] = {
      tier: t,
      kind: 'cosmetic',
      ref: fill.ref,
      label: fill.label,
      rarity: t >= 50 ? 5 : t >= 30 ? 4 : t >= 15 ? 3 : 2,
    };
  }

  // Coerce — every slot was filled above.
  return {
    free: free as SeasonReward[],
    premium: premium as SeasonReward[],
  };
}

/** All seasons keyed by season number. Append future seasons here; the
 *  legacy `SEASON_1_REWARDS` shape (single SeasonReward[] with `free`
 *  optional sub-object) is gone — readers must use SEASON_REWARDS or
 *  `currentSeasonRewards()`. */
export const SEASON_REWARDS: Record<number, SeasonRewardTrack> = {
  1: buildSeason1(),
};

/** Convenience accessor for the active season's tracks. */
export function currentSeasonRewards(): SeasonRewardTrack {
  const t = SEASON_REWARDS[CURRENT_SEASON_NUMBER];
  if (!t) throw new Error(`SEASON_REWARDS missing entry for season ${CURRENT_SEASON_NUMBER}`);
  return t;
}

export interface DailyChallengeTemplate {
  id: string;
  label: string;
  /** Target progress count (e.g. "win 2 rounds" → 2). */
  target: number;
  /** Reward in season XP + embers. */
  rewardSeasonXp: number;
  rewardEmbers: number;
}

export const DAILY_CHALLENGE_POOL: DailyChallengeTemplate[] = [
  // R2#73 rebalance: set-1 is the hardest single objective (was 500/250)
  // → bump to 750/350. streak-3 (hard, 3-in-a-row) was 400/200 → 500/250.
  // play-3-different is trivial (most rounds have 3 distinct games)
  // → cut from 350/150 to 200/75.
  { id: 'win-2',           label: 'Win 2 rounds',                     target: 2, rewardSeasonXp: 250, rewardEmbers: 100 },
  { id: 'play-5',          label: 'Play 5 rounds',                    target: 5, rewardSeasonXp: 250, rewardEmbers: 100 },
  { id: 'play-3-different',label: 'Play 3 different games',           target: 3, rewardSeasonXp: 200, rewardEmbers: 75  },
  { id: 'mvp-1',           label: 'Earn 1 MVP / award',               target: 1, rewardSeasonXp: 350, rewardEmbers: 150 },
  { id: 'streak-3',        label: 'Get a 3-round win streak',         target: 3, rewardSeasonXp: 500, rewardEmbers: 250 },
  { id: 'sxp-1500',        label: 'Earn 1,500 Season XP today',       target: 1500, rewardSeasonXp: 250, rewardEmbers: 100 },
  // R2#57: 'react-5' removed — no plumbing in ws-router/handleReaction to
  // tick a per-day reaction counter, so the challenge was uncompletable.
  // Re-add when we wire up a daily reaction tally row.
  { id: 'set-1',           label: 'Win 1 full set',                   target: 1, rewardSeasonXp: 750, rewardEmbers: 350 },
  { id: 'top2-3',          label: 'Place top-2 in 3 rounds',          target: 3, rewardSeasonXp: 400, rewardEmbers: 200 },
  { id: 'unlock-1',        label: 'Unlock any cosmetic',              target: 1, rewardSeasonXp: 350, rewardEmbers: 150 },

  // R2#75 — pool expansion (10 → 18 templates) so the deterministic
  // 3-pick has more variety and the perceived 3-day repeat fades. Each
  // new template MUST have a corresponding case in
  // lambdas/ws-router/src/index.ts daily-challenge progress switch.
  // Per-game variants (5 chosen for breadth across realtime / card /
  // physics / tactics):
  { id: 'play-tap-race',     label: 'Play 3 Tap Race rounds',         target: 3, rewardSeasonXp: 250, rewardEmbers: 100 },
  { id: 'play-coin-conveyer',label: 'Play 3 Coin Conveyer rounds',    target: 3, rewardSeasonXp: 250, rewardEmbers: 100 },
  { id: 'play-spice-rush',   label: 'Play 3 Spice Rush rounds',       target: 3, rewardSeasonXp: 250, rewardEmbers: 100 },
  { id: 'play-tarot-tap',    label: 'Play 3 Tarot Tap rounds',        target: 3, rewardSeasonXp: 250, rewardEmbers: 100 },
  { id: 'play-stick-brawl',  label: 'Play 3 Stick Brawl rounds',      target: 3, rewardSeasonXp: 250, rewardEmbers: 100 },
  // Generic stretch goals:
  { id: 'top-2',           label: 'Place top-2 in 3 rounds today',    target: 3, rewardSeasonXp: 400, rewardEmbers: 200 },
  { id: 'points-500-day',  label: 'Earn 500 round points today',      target: 500, rewardSeasonXp: 350, rewardEmbers: 150 },
  // 'login' is a single-round freebie — fires on the first round of the
  // day and gives the player something to claim immediately.
  { id: 'login',           label: 'Play any round today',             target: 1, rewardSeasonXp: 100, rewardEmbers: 50  },
  // 'rounds-10' is a play-5 stretch variant.
  { id: 'rounds-10',       label: 'Play 10 rounds today',             target: 10, rewardSeasonXp: 600, rewardEmbers: 300 },
];

/** Featured-game pool for the weekly playlist. Week-of-year picks one. */
export const WEEKLY_FEATURED_POOL: string[] = [
  'tap-race', 'tarot-tap', 'spice-rush', 'witch-switch', 'trace-race',
  'ouija-roulette', 'quick-draw', 'one-of-us', 'coin-conveyer', 'hexagon-heat',
  'last-lantern', 'lumber-tumble', 'climb-spire', 'burnstile', 'stick-brawl', 'draft-duel',
  'banner-bash', 'cellar-sweep', 'cipher-cellar', 'heist-heist', 'phantom-pursuit',
  'pirate-pulley', 'plinko-tavern', 'quill-race', 'relay-forge', 'rope-walk',
  'sky-sieve', 'splatter-square', 'stack-smack', 'trapline',
];

/** Featured game rotation. Used to be weekly which felt frozen because
 *  most casual players test only on a single day. Now rotates DAILY so
 *  the featured chip + XP boost move regularly. weekKey field kept for
 *  back-compat, but represents day-of-year now ("YYYY-D###"). */
export function featuredGameForWeek(date: Date = new Date()): { gameId: string; weekKey: string } {
  const start = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const dayOfYear = Math.floor((date.getTime() - start.getTime()) / 86400000);
  const weekKey = `${date.getUTCFullYear()}-D${String(dayOfYear).padStart(3, '0')}`;
  const idx = (dayOfYear + date.getUTCFullYear()) % WEEKLY_FEATURED_POOL.length;
  return { gameId: WEEKLY_FEATURED_POOL[idx]!, weekKey };
}

export const FEATURED_XP_MULTIPLIER = 1.5;

/** YYYY-MM-DD key for a daily challenge. Boundary is 5am UTC, NOT
 *  midnight UTC. Reason: pure-UTC rollover lands mid-evening local time
 *  for US players (8pm ET / 5pm PT for a late-DST UTC midnight), so
 *  late-evening play stamps progress against the UTC-next day. Players
 *  woke up "the next day" and saw their prior-evening completions
 *  already marked as today's — felt like the daily wasn't resetting.
 *  Shift the boundary 5 hours: rollover at 5am UTC = midnight CT / 1am
 *  ET / 9pm PT (prior day). All US timezones now see "today" line up
 *  with their wake/sleep cycle. The pickDailyChallenges seed still uses
 *  this same key, so two players in a room get the same daily set
 *  regardless of their device's local clock. */
const DAILY_BOUNDARY_OFFSET_HOURS = 5;
export function dailyKey(date: Date = new Date()): string {
  const shifted = new Date(date.getTime() - DAILY_BOUNDARY_OFFSET_HOURS * 3600_000);
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(shifted.getUTCDate()).padStart(2, '0')}`;
}

/** Deterministic 3-pick from the pool keyed on charId + date. */
export function pickDailyChallenges(charId: string, dateKey: string): DailyChallengeTemplate[] {
  const seed = hashString(`${charId}:${dateKey}`);
  const pool = [...DAILY_CHALLENGE_POOL];
  const out: DailyChallengeTemplate[] = [];
  let s = seed;
  for (let i = 0; i < DAILY_CHALLENGE_COUNT && pool.length > 0; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const idx = s % pool.length;
    const picked = pool.splice(idx, 1)[0];
    if (picked) out.push(picked);
  }
  return out;
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h >>> 0;
}
